<template>
    <template v-if="hasNotificationAccess && isDesktop">
        <CriticalNotification
            :notifications="notifications"
            :router="isAdminRoute ? adminRouter : router"
            @route="routeToNotification"
        />

        <ButtonModal
            ref="buttonModalRef"
            icon-class="i-bell relative"
            :button-class="
                'flex aspect-square items-center hover:bg-(--surface-highlight) notification-button border-none btn-link relative' +
                (subscriptions.length > 0
                    ? ' after:w-xs after:h-xs after:bg-(--state-danger-fg) after:rounded-full after:absolute after:top-0 after:right-0 after:animate-ping'
                    : '')
            "
            placement="right"
            transition
        >
            <div
                notificationv
                class="gap-xs px-sm flex max-h-[768px] w-[720px] flex-col overflow-x-hidden overflow-y-auto"
            >
                <div class="flex justify-between">
                    <h1>Notifications</h1>
                    <button
                        class="gap-xs btn-link flex"
                        @click="
                            (isAdminRoute ? adminRouter : router)
                                .push({
                                    name: 'notifications',
                                })
                                .then(closeModal)
                        "
                    >
                        Open notification overview
                    </button>
                </div>
                <NotificationList
                    :notifications="subscriptions"
                    :has-subscribed="notificationStore.hasSubscribed"
                    :notification-filter="[]"
                    :filtered-periods="[]"
                    @route="routeToNotification"
                />
            </div>
        </ButtonModal>
    </template>

    <div
        id="user-profile"
        name="user-profile"
        class="rotate-wrapper lg:px-2xs rounded-sm"
        :class="{
            'mx-sm mt-auto bg-(--surface-highlight)': !isDesktop,
            'lg:max-h-12': !isViewingImpersonationSubject,
            'impersonation-profile-chip': isViewingImpersonationSubject && isDesktop,
        }"
        @click="toggleshowUserOptions"
    >
        <div
            class="gap-xs p-xs lg:p-2xs flex items-center rounded-sm"
            :class="{
                'bg-lightest gap-sm': showUserOptions && isDesktop && !impersonationSessions,
                'gap-sm': impersonationSessions,
            }"
        >
            <AvatarArea v-if="impersonationSessions" :first-name="effectiveProfileName" size="smaller" />
            <AvatarArea
                v-else-if="!isAdminRoute"
                :first-name="organizationStore.user?.FIRST_NAME ?? organizationStore.user?.EMAIL!"
                :last-name="organizationStore.user?.LAST_NAME"
            />
            <AvatarArea
                v-else
                :first-name="adminStore.user?.FIRST_NAME ?? adminStore.user?.EMAIL!"
                :last-name="adminStore.user?.LAST_NAME"
            />
            <div class="flex min-w-0 flex-col">
                <p v-if="isViewingImpersonationSubject" class="impersonation-profile-label">Viewing as</p>
                <p class="text-md max-w-36 truncate">
                    {{
                        effectiveProfileName && effectiveProfileName.length > 20
                            ? effectiveProfileName.substring(0, 20) + '...'
                            : (effectiveProfileName ?? '')
                    }}
                </p>
                <p class="text-sm">{{ highestRole ?? '' }}</p>
            </div>

            <i
                class="i-dark-dropdown mr-2 ml-auto h-4 w-4 lg:mr-auto"
                :class="{
                    flipped: showUserOptions,
                }"
            />
        </div>

        <template v-if="isDesktop">
            <div
                v-if="showUserOptions"
                id="user-menu"
                class="user-menu p-sm absolute top-[82px] right-0 z-10 min-w-80 rounded-lg"
            >
                <ul id="user-options">
                    <template v-if="!lmsStore.isLMS">
                        <template v-if="canViewOrgProfile">
                            <li @click="routeToUserDetails">
                                <i class="i-user"></i>
                                <span>View profile</span>
                            </li>
                            <div class="border-b border-(--border-subtle)" />
                        </template>
                        <template v-if="darkModeEnabled">
                            <ThemeToggle class="px-sm py-sm" />
                            <div class="border-b border-(--border-subtle)" />
                        </template>
                        <template v-if="!currentlyOnAdmin">
                            <div class="pl-sm pt-6">
                                <label>Organizations</label>
                            </div>
                            <li
                                v-for="org in organizationStore.availableOrganizations"
                                :key="org.organization_id"
                                class="flex justify-between"
                                @click="(event) => handleOrgSwitch(event, org.organization_id)"
                            >
                                <span>{{ org.name }}</span>
                                <i
                                    :class="{
                                        'i-dark-check': org.organization_id === selectedOrgId,
                                    }"
                                />
                            </li>
                            <div class="border-b border-(--border-subtle)" />
                        </template>
                        <template v-if="hasAdminAccess">
                            <div class="pl-sm pt-6">
                                <label>Access</label>
                            </div>
                            <li
                                v-if="organizationStore.availableOrganizations.length > 0"
                                class="flex justify-between"
                                @click="handleAdminSwitch({ reqValue: false })"
                            >
                                <div class="gap-xs flex">
                                    <i class="i-site"></i>
                                    <span>Organization</span>
                                </div>
                                <i v-if="!isAdminRoute" class="i-dark-check" />
                            </li>
                            <li class="flex justify-between" @click="handleAdminSwitch({ reqValue: true })">
                                <div class="gap-xs flex">
                                    <i class="i-user"></i>
                                    <span>Admin</span>
                                </div>
                                <i v-if="isAdminRoute" class="i-dark-check" />
                            </li>
                            <div class="border-b border-(--border-subtle)" />
                        </template>
                        <template v-if="impersonationSessions">
                            <div class="pl-sm pt-6">
                                <label>Impersonation</label>
                            </div>
                            <li class="flex justify-between" @click="switchImpersonationSession('original')">
                                <div class="gap-xs flex flex-col">
                                    <span>Me</span>
                                    <span class="text-sm text-(--text-muted)">{{
                                        impersonationSessions.original.label
                                    }}</span>
                                </div>
                                <i v-if="impersonationSessions.active === 'original'" class="i-dark-check" />
                            </li>
                            <li class="flex justify-between" @click="switchImpersonationSession('subject')">
                                <div class="gap-xs flex flex-col">
                                    <span>Subject</span>
                                    <span class="text-sm text-(--text-muted)">{{ impersonationSessions.subject.label }}</span>
                                </div>
                                <i v-if="impersonationSessions.active === 'subject'" class="i-dark-check" />
                            </li>
                            <div class="border-b border-(--border-subtle)" />
                        </template>
                    </template>
                    <li @click="logoutHandler">
                        <i class="i-logout"></i>
                        <span>Logout</span>
                    </li>
                </ul>
            </div>
        </template>

        <template v-else>
            <!-- ITDEVELOP-3147: ui/sheet replaces the hand-rolled
                 mobile/bottom-sheet.vue, which had a fixed-fraction snap height
                 (half the viewport regardless of content), touch handlers
                 fighting its own scroll for the drag gesture, and no portal,
                 focus trap or dialog semantics. Grabber + content height +
                 rounded top keep its look. This menu lives inside the mobile
                 LAYOUT (the burger breakpoint), so it stays gated by isDesktop
                 rather than usePhoneSizedOverlays — it is the mobile layout's menu, not a
                 responsive fork of a desktop component. -->
            <Sheet v-model:open="showUserOptions">
                <SheetContent
                    side="bottom"
                    class="max-h-[80dvh] gap-0 overflow-y-auto rounded-t-lg p-0"
                    hide-close
                    data-testid="user-profile-sheet"
                >
                    <SheetTitle class="sr-only">Account</SheetTitle>
                    <div class="pt-sm gap-md flex h-full flex-col">
                    <template v-if="!lmsStore.isLMS">
                        <div v-if="canViewOrgProfile" class="gap-sm flex flex-col">
                            <button class="gap-xs flex w-full items-center" @click.stop="handleMobileProfileClick">
                                <i class="i-user block size-6" />
                                <span class="text-md">View profile</span>
                            </button>
                            <div class="border-b border-(--border-subtle)"></div>
                        </div>
                        <div v-if="darkModeEnabled" class="gap-sm flex flex-col">
                            <ThemeToggle class="px-sm py-xs" />
                            <div class="border-b border-(--border-subtle)"></div>
                        </div>
                        <div
                            v-if="!currentlyOnAdmin && organizationStore.availableOrganizations.length > 0"
                            class="gap-sm flex flex-col"
                        >
                            <p class="px-sm text-(--text-muted)">Organizations</p>
                            <li
                                v-for="org in organizationStore.availableOrganizations"
                                :key="org.organization_id"
                                class="px-sm py-xs flex items-center justify-between"
                                @click.stop="(event) => handleOrgSwitch(event, org.organization_id)"
                            >
                                <p class="text-md">{{ org.name }}</p>
                                <i
                                    :class="{
                                        'i-dark-check': org.organization_id === selectedOrgId,
                                    }"
                                />
                            </li>
                            <div class="border-b border-(--border-subtle)"></div>
                        </div>
                        <div v-if="hasAdminAccess" class="gap-sm flex flex-col">
                            <p class="px-sm text-(--text-muted)">Access</p>
                            <button
                                v-if="organizationStore.availableOrganizations.length > 0"
                                class="px-sm py-xs gap-sm flex w-full items-center justify-between"
                                @click.stop="handleAdminSwitch({ reqValue: false })"
                            >
                                <span class="gap-xs flex items-center">
                                    <i class="i-site size-5" />
                                    <span class="text-md">Organization</span>
                                </span>
                                <i v-if="!isAdminRoute" class="i-dark-check" />
                            </button>
                            <button
                                class="px-sm py-xs gap-sm flex w-full items-center justify-between"
                                @click.stop="handleAdminSwitch({ reqValue: true })"
                            >
                                <span class="gap-xs flex items-center">
                                    <i class="i-user size-5" />
                                    <span class="text-md">Admin</span>
                                </span>
                                <i v-if="isAdminRoute" class="i-dark-check" />
                            </button>
                            <div class="border-b border-(--border-subtle)"></div>
                        </div>
                        <div v-if="impersonationSessions" class="gap-sm flex flex-col">
                            <p class="px-sm text-(--text-muted)">Impersonation</p>
                            <button
                                class="px-sm py-xs gap-sm flex w-full items-center justify-between"
                                @click.stop="switchImpersonationSession('original')"
                            >
                                <span class="flex flex-col text-left">
                                    <span class="text-md">Me</span>
                                    <span class="text-sm text-(--text-muted)">{{
                                        impersonationSessions.original.label
                                    }}</span>
                                </span>
                                <i v-if="impersonationSessions.active === 'original'" class="i-dark-check" />
                            </button>
                            <button
                                class="px-sm py-xs gap-sm flex w-full items-center justify-between"
                                @click.stop="switchImpersonationSession('subject')"
                            >
                                <span class="flex flex-col text-left">
                                    <span class="text-md">Subject</span>
                                    <span class="text-sm text-(--text-muted)">{{ impersonationSessions.subject.label }}</span>
                                </span>
                                <i v-if="impersonationSessions.active === 'subject'" class="i-dark-check" />
                            </button>
                            <div class="border-b border-(--border-subtle)"></div>
                        </div>
                    </template>
                    <div class="gap-sm flex flex-col">
                        <button class="gap-sm flex w-full items-center" @click.stop="logoutHandler">
                            <i class="i-logout size-5" />
                            <span class="text-md">Log out</span>
                        </button>
                    </div>
                    </div>
                </SheetContent>
            </Sheet>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted, useTemplateRef } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useIsDesktop } from '@/composables/isDesktop';
import { useOrganizationStore } from '@/stores/organization';
import { useLMSStore } from '@/stores/lms';
import { isFeatureEnabled } from '@/config/featureFlags';
import { resToNotification } from '@/stores/helpers';

import router from '@/router';
import adminRouter from '@/router/admin-router';
import AvatarArea from './avatar-area.vue';
import ThemeToggle from './theme-toggle.vue';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import CriticalNotification from './misc/critical-notification.vue';
import { useAdminStore } from '@/stores/admin';
import { NOTIFICATION_SEVERITY } from '@/stores/store-interfaces';
import ButtonModal from './misc/button-modal.vue';
import NotificationList from './misc/notification-list.vue';
import { useNotificationStore } from '@/stores/notification';
import { canViewOrganizationProfile, getDefaultAdminRouteName, hasAdminMenuAccess } from '@/router/admin-access';

localStorage.setItem('admin', localStorage.getItem('admin') || 'false');
const notificationStore = useNotificationStore();
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const adminStore = useAdminStore();
const lmsStore = useLMSStore();
const darkModeEnabled = isFeatureEnabled('darkMode');
const buttonModalRef = useTemplateRef('buttonModalRef');
const selectedOrgId = computed(() => organizationStore.usersOrganization?.organization_id);
const showUserOptions = ref(false);
const isDesktop = useIsDesktop();
const isAdminRoute = computed(() => JSON.parse(localStorage.getItem('admin') ?? ''));
const impersonationSessions = computed(() => authStore.impersonationSessions);
const isViewingImpersonationSubject = computed(() => impersonationSessions.value?.active === 'subject');
const currentlyOnAdmin = computed(() => localStorage.getItem('admin') === 'true');
const canViewOrgProfile = computed(() =>
    canViewOrganizationProfile({
        isAdminRoute: currentlyOnAdmin.value,
        isLMS: lmsStore.isLMS,
    }),
);
const userCPRoles = computed(() => adminStore.adminRoleContext.userCPRoles);
const hasNotificationAccess = computed(() => notificationStore.hasNotificationAccess);
const emit = defineEmits<{
    (e: 'close'): void;
}>();

const highestRole = computed(() => {
    if (currentlyOnAdmin.value) {
        return ['admin', 'cdim-admin', 'noc-admin'].find((role) => userCPRoles.value.includes(role));
    } else {
        const possibleOrg = organizationStore.user?.organizations.find(
            (org) => org.organization_id === selectedOrgId?.value,
        );
        if (possibleOrg && possibleOrg.organization_roles.some((role: { name: string }) => role.name === 'org-admin')) {
            return 'org-admin';
        } else return 'User';
    }
});

const profileName = computed(() => {
    if (currentlyOnAdmin.value) {
        const userCopy = (adminStore.user ?? {}) as Partial<{
            first_name: string;
            last_name: string;
            email: string;
        }>;
        if (userCopy.first_name === undefined && userCopy.last_name === undefined) {
            return userCopy?.email ?? 'No name';
        } else {
            return userCopy.first_name + ' ' + userCopy.last_name;
        }
    } else {
        return organizationStore.usersOrganization?.name ?? '';
    }
});
const effectiveProfileName = computed(() => {
    if (!impersonationSessions.value) {
        return profileName.value;
    }

    return impersonationSessions.value.active === 'original'
        ? impersonationSessions.value.original.label
        : impersonationSessions.value.subject.label;
});
const hasAdminAccess = computed(() => hasAdminMenuAccess(adminStore.adminRoleContext));

const notificationOrgId = computed(() => {
    return notificationStore.filteredNotificationOrganizations?.[0]?.organization_id;
});

const notifications = computed(() => {
    return notificationStore.orgNotifications
        .map(resToNotification)
        .filter((n) => n.severity === NOTIFICATION_SEVERITY.CRITICAL);
});
const subscriptions = computed(() =>
    notificationStore.subscriptions.map(resToNotification).filter((n) => n.acknowledged === 0),
);

const routeToNotification = (notificationId: string) => {
    (isAdminRoute.value ? adminRouter : router).push({
        name: 'notification-details',
        params: { id: notificationId, orgId: notificationOrgId.value },
    });
};

watch(notificationOrgId, async () => {
    if (hasNotificationAccess.value) {
        await notificationStore.setup();
    }
});

onMounted(async () => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleEscKey);
});

const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && showUserOptions.value) {
        showUserOptions.value = false;
    }
};

const handleClickOutside = (event: MouseEvent) => {
    const userMenu = document.getElementById('user-menu');
    const dropdown = document.getElementById('user-profile');
    const target = event.target;

    if (!(target instanceof Node)) {
        return;
    }

    if (userMenu && dropdown && !dropdown.contains(target) && !userMenu.contains(target)) {
        showUserOptions.value = false;
    }
};

function toggleshowUserOptions() {
    showUserOptions.value = !showUserOptions.value;
}

async function handleOrgSwitch(event: MouseEvent, requestedOrgID: number) {
    localStorage.setItem('organization_id', requestedOrgID.toString());
    showUserOptions.value = false;
    emit('close');

    if (window.location.pathname !== '/') {
        router.push('/').then(() => {
            window.location.reload();
        });
    } else window.location.reload();
}

const logoutHandler = () => {
    authStore.logout();
};

const switchImpersonationSession = async (target: 'original' | 'subject') => {
    closeUserOptions();
    emit('close');
    await authStore.switchImpersonationSession(target);
};

const routeToUserDetails = () => {
    router.push({
        name: 'base-user',
        params: { userId: organizationStore.user?.ID },
    });
};

const handleAdminSwitch = ({ reqValue }: { reqValue: boolean }) => {
    if (JSON.parse(localStorage.getItem('admin') ?? '') !== reqValue) {
        const defaultAdminRouteName = getDefaultAdminRouteName(adminStore.adminRoleContext);
        if (reqValue && defaultAdminRouteName === undefined) {
            return;
        }

        showUserOptions.value = false;
        emit('close');
        localStorage.setItem('admin', reqValue.toString());
        const currentRouter = reqValue ? adminRouter : router;
        const target = reqValue ? { name: defaultAdminRouteName } : { path: '/' };

        currentRouter.push(target).then(() => {
            window.location.reload();
        });
    }
};

const closeModal = () => {
    buttonModalRef.value?.toggleModal();
};

const closeUserOptions = () => {
    showUserOptions.value = false;
};

const handleMobileProfileClick = () => {
    if (!canViewOrgProfile.value) {
        emit('close');
        return;
    }

    routeToUserDetails();
    emit('close');
};
</script>

<style scoped>
@reference '@/assets/styles/default.css';

.user-menu {
    @apply p-sm absolute right-0 z-10 min-w-80 rounded-lg;
    /* Popover treatment, not a hand-rolled light shadow: --ring-popover keeps
       the soft drop shadow in light and adds the hairline + strong shadow that
       give the panel a boundary on dark chrome (ITDEVELOP-1450 QA). */
    background: var(--overlay-surface);
    box-shadow: var(--ring-popover);

    ul {
        li {
            @apply p-sm gap-xs hover:bg-(--surface-highlight) flex cursor-pointer rounded-sm font-light transition-colors duration-300;
        }

        > div > label {
            @apply text-(--text-muted);
        }
    }
}

.impersonation-profile-chip {
    --impersonation-warning-light: var(--color-orange-50);
    --impersonation-warning-dark: rgb(234 115 23);
    border: 1px solid var(--impersonation-warning-dark);
    background: var(--impersonation-warning-light);
}

.impersonation-profile-chip:hover {
    background: var(--impersonation-warning-light);
}

.impersonation-profile-label {
    color: var(--color-orange-500);
    font-size: 10px;
    font-weight: 800;
    line-height: 12px;
    text-transform: uppercase;
}

.bottom-line {
    @apply border-b border-(--border-subtle);
}
</style>
